import { useEffect, useMemo, useRef, useState } from "react";
import Hls from "hls.js";
import StudentLayout from "@/components/StudentLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Camera, ShieldCheck, AlertTriangle, Loader2, Radio, Lock, RefreshCw,
  Volume2, VolumeX, Maximize, PlayCircle, WifiOff,
} from "lucide-react";

/* Discourage right-click "save video as" — cannot be relied on for security
   (the real access control is the short-lived JWT + gateway auth). */
const noContext = (e) => e.preventDefault();

export default function StudentLiveClassroom() {
  const [status, setStatus] = useState(null);      // /status result
  const [loading, setLoading] = useState(true);
  const [pin, setPin] = useState("");
  const [starting, setStarting] = useState(false);
  const [session, setSession] = useState(null);
  const [playerError, setPlayerError] = useState("");
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const refreshTimer = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/live-classroom/status");
      setStatus(data);
    } catch (e) {
      toast.error("Could not load Live Classroom.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); return () => stopStream(true); /* eslint-disable-next-line */ }, []);

  // Auto-refresh session token before expiry.
  const scheduleRefresh = (sec) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(refreshSession, Math.max(30, sec) * 1000);
  };

  const attachHls = (url) => {
    const video = videoRef.current;
    if (!video) return;
    try { hlsRef.current?.destroy(); } catch {}
    hlsRef.current = null;
    setPlayerError("");
    if (Hls.isSupported()) {
      const hls = new Hls({ lowLatencyMode: true, backBufferLength: 8, maxBufferLength: 12 });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_e, data) => {
        if (data.fatal) {
          setPlayerError("Connection lost. Attempting to reconnect…");
          // exponential-ish reconnect via refreshSession → new signed URL
          setTimeout(refreshSession, 2500);
        }
      });
      hlsRef.current = hls;
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari iOS: native HLS.
      video.src = url;
    } else {
      setPlayerError("Your browser does not support HLS live streaming.");
    }
    video.muted = true;
    video.play().catch(() => {/* autoplay blocked — user taps play */});
  };

  const startStream = async () => {
    if (starting) return;
    setStarting(true);
    setPlayerError("");
    try {
      const body = status?.parent_pin?.required ? { pin } : {};
      const { data } = await api.post("/live-classroom/session", body);
      setSession(data);
      attachHls(data.stream_playback_url);
      scheduleRefresh(data.refresh_after_sec || 240);
    } catch (e) {
      const code = e?.response?.data?.detail || e?.message || "Could not start stream.";
      if (code === "PARENT_PIN_REQUIRED") toast.error("Please enter the Parent PIN.");
      else if (code === "PARENT_PIN_INVALID") { toast.error("Wrong Parent PIN. Please try again."); setPin(""); }
      else if (code === "PARENT_PIN_NOT_SET") toast.error("Ask the admin to set the Parent PIN for your student account.");
      else toast.error(typeof code === "string" ? code : "Could not start stream.");
    } finally { setStarting(false); }
  };

  const refreshSession = async () => {
    if (!session?.session_id) return;
    try {
      const { data } = await api.post("/live-classroom/session/refresh", { session_id: session.session_id });
      setSession((s) => ({ ...s, ...data }));
      attachHls(data.stream_playback_url);
      scheduleRefresh(data.refresh_after_sec || 240);
    } catch (e) {
      setPlayerError(e?.response?.data?.detail || "Live Classroom viewing has ended.");
      stopStream();
    }
  };

  const stopStream = (silent = false) => {
    if (refreshTimer.current) { clearTimeout(refreshTimer.current); refreshTimer.current = null; }
    try { hlsRef.current?.destroy(); } catch {}
    hlsRef.current = null;
    if (session?.session_id) {
      api.post("/live-classroom/session/end", { session_id: session.session_id }).catch(() => {});
    }
    setSession(null);
    if (!silent) setPlayerError("");
  };

  const toggleMute = () => {
    const v = videoRef.current; if (!v) return;
    v.muted = !v.muted; setMuted(v.muted);
  };
  const goFullscreen = () => {
    const v = videoRef.current;
    if (v?.requestFullscreen) v.requestFullscreen();
    else if (v?.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  return (
    <StudentLayout>
      <div className="max-w-4xl mx-auto space-y-4" data-testid="student-live-classroom">
        <Header status={status} />
        {loading ? (
          <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : !status ? null : (
          <Body
            status={status} session={session} playerError={playerError} pin={pin} setPin={setPin}
            onStart={startStream} onStop={() => stopStream()} onRefresh={refreshSession}
            starting={starting} videoRef={videoRef} muted={muted} onToggleMute={toggleMute} onFullscreen={goFullscreen}
            onContextMenu={noContext}
          />
        )}
      </div>
    </StudentLayout>
  );
}

function Header({ status }) {
  const s = status?.student, c = status?.camera;
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white dark:bg-slate-900 flex flex-wrap items-center gap-3" data-testid="live-header">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="h-11 w-11 rounded-xl bg-rose-100 dark:bg-rose-950/40 flex items-center justify-center shrink-0">
          <Camera className="h-6 w-6 text-rose-500" />
        </div>
        <div className="min-w-0">
          <div className="text-lg font-semibold flex items-center gap-2">Live Classroom</div>
          <p className="text-xs text-slate-500">For Parent Viewing Only</p>
        </div>
      </div>
      {s ? (
        <div className="text-right text-sm">
          <div className="font-semibold" data-testid="live-student-name">{s.name}</div>
          <div className="text-slate-500 text-xs">Class {s.class_name}-{s.section} · Roll {s.roll_number}</div>
          {c ? <div className="mt-1 text-[11px] text-slate-500">Camera: {c.camera_name}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

function Body({ status, session, playerError, pin, setPin, onStart, onStop, onRefresh, starting, videoRef, muted, onToggleMute, onFullscreen, onContextMenu }) {
  const state = status.state;

  if (state === "disabled_emergency") return <Message icon={AlertTriangle} tone="rose" title="Live Classroom is temporarily disabled" text="The school has turned off live streaming. Please try again later." />;
  if (state === "no_camera") return <Message icon={WifiOff} tone="slate" title="Not configured" text="Live streaming is not configured for this classroom yet." />;
  if (state === "outside_schedule") {
    const sc = status.schedule || {};
    return <Message icon={AlertTriangle} tone="amber" title="Outside viewing hours" text={`Live Classroom is available only during school-approved viewing hours (${sc.start || "09:00"} – ${sc.end || "16:00"}).`} />;
  }

  const pinNeeded = status?.parent_pin?.required;
  const pinSet = status?.parent_pin?.set;

  return (
    <div className="space-y-4">
      {/* Video card */}
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-black overflow-hidden relative aspect-video" data-testid="live-player-card">
        {session ? (
          <>
            <video
              ref={videoRef} autoPlay playsInline muted={muted}
              className="w-full h-full object-contain bg-black"
              onContextMenu={onContextMenu} controlsList="nodownload noremoteplayback"
              data-testid="live-video"
            />
            <div className="absolute top-3 left-3 flex items-center gap-2 bg-rose-500/90 text-white px-2.5 py-1 rounded-full text-xs font-bold shadow" data-testid="live-badge">
              <Radio className="h-3.5 w-3.5 animate-pulse" /> LIVE
            </div>
            <div className="absolute bottom-3 right-3 flex items-center gap-2">
              <button onClick={onToggleMute} className="h-9 w-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80" title={muted ? "Unmute" : "Mute"} data-testid="live-mute">
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
              <button onClick={onRefresh} className="h-9 w-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80" title="Reconnect" data-testid="live-reconnect">
                <RefreshCw className="h-4 w-4" />
              </button>
              <button onClick={onFullscreen} className="h-9 w-9 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80" title="Fullscreen" data-testid="live-fullscreen">
                <Maximize className="h-4 w-4" />
              </button>
              <button onClick={onStop} className="h-9 px-3 rounded-full bg-rose-500 text-white flex items-center justify-center hover:bg-rose-600 text-xs font-semibold" data-testid="live-stop">
                Stop
              </button>
            </div>
            {playerError ? (
              <div className="absolute inset-x-0 bottom-16 text-center text-white/95 text-sm bg-black/60 py-2 px-3">{playerError}</div>
            ) : null}
          </>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-slate-300 p-6 text-center">
            <PlayCircle className="h-14 w-14 mb-3 opacity-70" />
            <div className="text-sm">Tap <span className="text-white font-semibold">Start Live Stream</span> to watch your child's classroom.</div>
            {playerError ? <div className="mt-2 text-rose-300 text-xs">{playerError}</div> : null}
          </div>
        )}
      </div>

      {/* Start / PIN card */}
      {!session ? (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-5 bg-white dark:bg-slate-900 space-y-3" data-testid="live-start-card">
          {pinNeeded && !pinSet ? (
            <div className="text-sm text-amber-600 flex items-center gap-2" data-testid="pin-not-set">
              <ShieldCheck className="h-4 w-4" /> Ask your school admin to set the Parent PIN for your account before viewing.
            </div>
          ) : null}
          {pinNeeded && pinSet ? (
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1"><Lock className="h-3 w-3" /> Parent PIN</Label>
              <Input
                type="password" inputMode="numeric" maxLength={8} pattern="[0-9]*"
                value={pin} onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))}
                className="rounded-xl mt-1" placeholder="4–8 digits" autoComplete="off"
                data-testid="pin-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">Ask the school admin if you don't have this yet.</p>
            </div>
          ) : null}
          <Button
            className="w-full rounded-full bg-rose-500 hover:bg-rose-600"
            onClick={onStart}
            disabled={starting || (pinNeeded && pinSet && pin.length < 4)}
            data-testid="live-start"
          >
            {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Radio className="h-4 w-4 mr-2" />}
            Start Live Stream
          </Button>
          <p className="text-[11px] text-slate-500 text-center">
            The stream is a private, encrypted view of your child's classroom during school hours. Sessions expire automatically for privacy.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function Message({ icon: Icon, tone, title, text }) {
  const tones = {
    rose:  "bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-900/40",
    amber: "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-900/40",
    slate: "bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800",
  };
  return (
    <div className={`rounded-2xl border p-5 flex items-start gap-3 ${tones[tone] || tones.slate}`} data-testid="live-message">
      <Icon className="h-5 w-5 mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-sm mt-1 opacity-90">{text}</div>
      </div>
    </div>
  );
}
